import create from 'zustand';
import { FilterType } from './useFilters';
import {
  IPageFilterRendererDataRecord,
  PerformanceFilterOptionTypeEnum,
} from '@diana/core';
import { getDeviceName } from '../utils/stringUtils';

interface FilterOptionsStore {
  data: any;
  barges: any;
  devicesById: Record<
    string,
    {
      id: string;
      name: string;
      bargeName: string;
    }
  > | null;
  dataGroupedByBarge: {
    barges: Barge[];
  } | null;
  setData: (data: any) => void;
  reset: () => void;
}

export interface Device {
  id: string;
  name: string;
  bunkerMeterTicketNumbers: string[];
}

export interface Barge {
  name: string;
  devices: Device[];
  systemId: string;
}

interface InputObject {
  barges: Barge[];
}

interface Option {
  displayName: string;
  value: string;
}

interface OutputObject {
  [key: string]: IPageFilterRendererDataRecord[];
}

// const readFiltersFromQuery = (availableFilters: IFilter[], data: any): IExtendedFiltersValues => {
//   const filtersValues: IExtendedFiltersValues = {
//     [FilterType.TIME_RANGE]: [],
//     [FilterType.BARGES]: [],
//     [FilterType.MFM_DEVICES]: [],
//     [FilterType.BMT_NUMBER]: [],
//   };
//   const urlParams = new URLSearchParams(window.location.search);
//
//   // Read non-time-range filters
//   availableFilters.forEach((filter) => {
//     const key = filter.property.key;
//     if (key !== FilterType.TIME_RANGE && urlParams.has(key)) {
//       const paramValue = urlParams.get(key);
//       if (data[key]) {
//         data[key][0].options.forEach((item: any) => {
//           if (item.value === paramValue || item.displayName === paramValue) {
//             filtersValues[key].push({ value: item.value, displayName: item.displayName });
//           } else {
//             paramValue?.split(',').forEach((value) => {
//               if (item.value === value || item.displayName === value) {
//                 filtersValues[key].push({ value: item.value, displayName: item.displayName });
//               }
//             });
//           }
//         });
//       }
//     }
//   });
//
//   // Handle time range
//   if (hasTimeRangeInQuery()) {
//     if (urlParams.has('preset')) {
//       const preset = urlParams.get('preset') as CalendarDatePickerPreset;
//       const value = presetToValue({ preset });
//       value?.forEach((item) => {
//         if (item !== null) {
//           filtersValues[FilterType.TIME_RANGE].push({
//             value: DateTime.fromFormat(item, "yyyy-MM-dd'T'HH:mm").toUTC() as TFilterValue,
//             displayName: item as TFilterDisplayName,
//             preset,
//           });
//         }
//       });
//     } else {
//       if (urlParams.has('from')) {
//         const value = urlParams.get('from');
//         filtersValues[FilterType.TIME_RANGE].push({
//           value: value as TFilterValue,
//           displayName: value as TFilterDisplayName,
//         });
//       }
//       if (urlParams.has('to')) {
//         const value = urlParams.get('to');
//         filtersValues[FilterType.TIME_RANGE].push({
//           value: value as TFilterValue,
//           displayName: value as TFilterDisplayName,
//         });
//       }
//     }
//   }
//
//   return filtersValues;
// };

const transformInput = (input: InputObject): OutputObject => {
  const output: OutputObject = {};

  // Extract barge names
  const bargeNames: Option[] = input.barges.map((barge) => ({
    displayName: barge.name,
    value: barge.systemId,
  }));

  output[FilterType.BARGES] = [
    {
      key: FilterType.BARGES,
      type: PerformanceFilterOptionTypeEnum.ENUM,
      options: bargeNames,
    },
  ];

  // Extract device names
  const deviceNames: Option[] = input.barges.flatMap((barge) =>
    barge.devices.map((device) => ({
      displayName: getDeviceName({ barge, device }),
      value: device.id,
    })),
  );

  output[FilterType.MFM_DEVICES] = [
    {
      key: FilterType.MFM_DEVICES,
      type: PerformanceFilterOptionTypeEnum.ENUM,
      options: deviceNames,
    },
  ];

  //Extract bunkerMeterTicketNumbers
  const bunkerMeterTicketNumbers: Option[] = input.barges.flatMap((barge) =>
    barge.devices.flatMap((device) =>
      device.bunkerMeterTicketNumbers.map((bunkerMeterTicketNumber) => ({
        displayName: bunkerMeterTicketNumber,
        value: bunkerMeterTicketNumber,
        deviceId: device.id,
      })),
    ),
  );

  output[FilterType.BMT_NUMBER] = [
    {
      key: FilterType.BMT_NUMBER,
      type: PerformanceFilterOptionTypeEnum.ENUM,
      options: bunkerMeterTicketNumbers,
    },
  ];

  return output;
};

export const useFilterOptions = create<FilterOptionsStore>((set) => ({
  data: null,
  dataGroupedByBarge: null, // Initialize to null or undefined
  barges: null,
  devicesById: null,
  setData: (data) => {
    const transformedData = transformInput(data);
    const devicesById: Record<string, { id: string; name: string; bargeName: string }> = {};

    data.barges.forEach((barge: any) => {
      barge.devices.forEach((device: any) => {
        devicesById[device.id] = {
          id: device.id,
          name: getDeviceName({ barge, device }),
          bargeName: barge.name,
        };
      });
    });

    set({
      data: transformedData,
      dataGroupedByBarge: data,
      devicesById,
      barges: data.barges,
    });
  },
  reset: () => set({ data: null, dataGroupedByBarge: null, barges: null, devicesById: null }),
}));
